import React, { useEffect, useRef } from 'react';

import { IVariation, IVariationContainerFields } from '@/types/contentful';

import { useOptimizely, DEFAULT_VARIATION } from '../../context/Optimizely';

import { Variation } from '../Variation';
import { trackWithFingerprint } from '~/utils/trackWithFingerprint';
import { trackRumEvent } from '~/utils/datadogRum';
import { usePageDataContext } from '~/context/PageData';
import { registerExperimentSuperProperty } from '~/utils/mixpanel';

interface VariationContainerProps extends IVariationContainerFields {
  sysId?: string;
  isBodyElement?: boolean;
}

export function VariationContainer({
  experimentKey = '',
  variations,
  sysId,
  assignControlAsDefault = true,
  audience,
  audienceKeyword,
  isBodyElement = false
}: VariationContainerProps) {
  const experiments = useOptimizely();
  const {
    query: { utm_source = '' } = {},
    cookies: { utm_source: cookieSource = '' } = {},
    isReturning
  } = usePageDataContext();

  /*
  This is the main logic for determining which variation to display.
  If the experiment is enabled and has a valid variation key set , that variation is displayed.
  If the experiment is not enabled, or no variationKey is found, the control variation is displayed, unless assignControlAsDefault is set to false.
  */

  const containerRef = useRef<HTMLDivElement>(null);
  let variationKey: string | undefined;
  // Audience gating logic
  let audienceMatch = false;

  if (!audience) {
    // No audience specified, proceed as normal
    audienceMatch = true;
  } else if (audience === 'utm_source') {
    // Check if utm_source in URL matches audienceKeyword
    audienceMatch =
      utm_source === audienceKeyword || cookieSource === audienceKeyword;
  } else if (audience === 'is_returning') {
    audienceMatch = isReturning || false; // Check if the user is returning
  }

  if (
    experiments[experimentKey]?.enabled &&
    experiments[experimentKey]?.variationKey
  ) {
    if (audienceMatch) {
      // If the experiment is enabled and audience matches, use the variationKey from Optimizely
      variationKey = experiments[experimentKey].variationKey;
    } else {
      // If the audience does not match, assing control as default
      variationKey = DEFAULT_VARIATION;
    }
  } else {
    variationKey = assignControlAsDefault ? DEFAULT_VARIATION : undefined;
  }

  useEffect(() => {
    const sessionExp = sessionStorage.getItem(`gl_${experimentKey}`);
    const observer = new IntersectionObserver(([entry]) => {
      if (
        entry.isIntersecting &&
        experiments[experimentKey]?.enabled &&
        (!sessionExp || sessionExp !== variationKey)
      ) {
        // Register experiment as super property to ensure it's included with all events
        registerExperimentSuperProperty(experimentKey, variationKey || '');

        trackWithFingerprint('Website Experiment Loaded', {
          experiment_key: experimentKey,
          variation_name: variationKey
        });
        trackRumEvent('Website Experiment Loaded', {
          experiment_key: experimentKey,
          variation_name: variationKey
        });
        sessionStorage.setItem(`gl_${experimentKey}`, variationKey || '');
        observer.disconnect();
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [experimentKey, variationKey, experiments]);

  return (
    <div
      ref={containerRef}
      className={`
      vc-${experimentKey}
      `}
      data-elid={sysId}
      data-expid={experimentKey}
      data-testid="variation-container"
    >
      {variations?.map(({ sys, fields }: IVariation) => {
        if (
          sys?.contentType?.sys?.id === 'variation' &&
          variationKey === fields.variationKey
        ) {
          return (
            <Variation
              {...fields}
              key={sys?.id}
              className={`vc-${experimentKey}__variation--${fields.variationKey} vc-${experimentKey}__variation`}
              isActive={variationKey === fields.variationKey}
              isBodyElement={isBodyElement}
            />
          );
        } else {
          return null;
        }
      })}
    </div>
  );
}
